import { useComputeCapacityPlanMutation } from 'core/graphql/CapacityPlanning/ComputeCapacityPlan.generated';
import { useRequestCapacityPlanGeocodingMutation } from 'core/graphql/CapacityPlanning/RequestCapacityPlanGeocoding.generated';
import { useRetryFailedCapacityPlanSolvesMutation } from 'core/graphql/CapacityPlanning/RetryFailedCapacityPlanSolves.generated';
import { toast } from 'react-toastify';
import useCapacityPlanUrl from './useCapacityPlanUrl';

/** Queries a compute or a Solve retry makes stale. */
const runQueries = ['CapacityPlanRun', 'CapacityPlanRuns'];

/** A geocoding request also changes the Delivery rows and their issue groups. */
const geocodeQueries = [
  ...runQueries,
  'CapacityPlanDeliveries',
  'CapacityPlanDeliveryIssues',
];

/**
 * Every refusal of this feature is one `CapacityPlanUserError` carrying a
 * `CapacityPlanErrorCode`; the message is written for the supervisor, so it is
 * shown as it comes.
 */
type PayloadErrors = ReadonlyArray<{ message: string }> | null | undefined;

const firstError = (errors: PayloadErrors, fallback: string): string | null =>
  errors && errors.length > 0 ? errors[0].message ?? fallback : null;

export interface CapacityPlanActions {
  /** Enqueues one Solve per Planning Depot; on success the Result tab opens. */
  compute: () => Promise<void>;
  /** Sends the never-requested and Failed Deliveries into geocoding. */
  requestGeocoding: () => Promise<void>;
  /** Puts only the Failed Solves of a computed Run back on the queue. */
  retryFailedSolves: () => Promise<void>;
  isComputing: boolean;
  isRequestingGeocoding: boolean;
  isRetryingSolves: boolean;
}

/**
 * The supervisor actions on one Run, with the toasts and the navigation they
 * share. Every mutation reports a refusal in its payload rather than by
 * rejecting, so each handler checks `errors` before claiming success. The busy
 * flags come straight from Apollo, so there is no duplicate status in Redux.
 */
const useCapacityPlanActions = (runId: string): CapacityPlanActions => {
  const { setTab } = useCapacityPlanUrl();
  const [computeCapacityPlan, { loading: isComputing }] =
    useComputeCapacityPlanMutation({
      refetchQueries: runQueries,
      awaitRefetchQueries: true,
    });
  const [requestGeocodingMutation, { loading: isRequestingGeocoding }] =
    useRequestCapacityPlanGeocodingMutation({
      refetchQueries: geocodeQueries,
    });
  const [retryFailedSolvesMutation, { loading: isRetryingSolves }] =
    useRetryFailedCapacityPlanSolvesMutation({
      refetchQueries: runQueries,
      awaitRefetchQueries: true,
    });

  const compute = async () => {
    try {
      const { data } = await computeCapacityPlan({
        variables: { input: { runId } },
      });
      const message = firstError(
        data?.computeCapacityPlan?.errors,
        'The plan could not be computed.',
      );
      if (message) {
        toast.error(message);
        return;
      }
      // The run is now Queued, and the Result tab is where its progress shows.
      setTab('result');
    } catch {
      toast.error('The plan could not be computed.');
    }
  };

  const requestGeocoding = async () => {
    try {
      const { data } = await requestGeocodingMutation({
        variables: { input: { runId } },
      });
      const message = firstError(
        data?.requestCapacityPlanGeocoding?.errors,
        'The geocoding could not be requested.',
      );
      if (message) {
        toast.error(message);
        return;
      }
      const requested =
        data?.requestCapacityPlanGeocoding?.capacityPlanGeocoding?.requested ??
        0;
      toast.success(`${requested} rows were sent to address processing.`);
    } catch {
      toast.error('The geocoding could not be requested.');
    }
  };

  const retryFailedSolves = async () => {
    try {
      const { data } = await retryFailedSolvesMutation({
        variables: { input: { runId } },
      });
      const message = firstError(
        data?.retryFailedCapacityPlanSolves?.errors,
        'The failed solves could not be retried.',
      );
      if (message) {
        toast.error(message);
        return;
      }
      toast.success('The failed solves were put back on the queue.');
    } catch {
      toast.error('The failed solves could not be retried.');
    }
  };

  return {
    compute,
    requestGeocoding,
    retryFailedSolves,
    isComputing,
    isRequestingGeocoding,
    isRetryingSolves,
  };
};

export default useCapacityPlanActions;
